import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectOffers,
  selectCurrentProductId,
} from 'shared/store/product/selector';
import { fetchProductOffers } from 'shared/store/product/action';
import { State } from 'store/types';
import { deviceTypes, platformNames } from 'shared/constants';
import { OFFERS_SECTION_ID } from 'constants/productPage';
import ShowMore from './ShowMore';
import { useMiniCouponsAndOffersEligibility } from 'hooks/Product/coupons';
import {
  Divider,
  Dot,
  HeadingWrapper,
  MiniOffersWrapper,
  OfferHeading,
  OfferList,
  OfferSkeleton,
  OfferText,
  Wrapper,
} from './styled';
import { Offer } from 'store/product/types';
import OfferTileV2 from '@nykaa/offers/components/OfferTileV2';
import { DOT_ICON, getOfferListingUrl, toShowViewProducts } from './helper';

const Offers = () => {
  const dispatch = useDispatch();
  const [numberOfCoupons, setNumberOfCoupons] = useState(1);
  const [title, setTitle] = useState('View more offers');
  const authData = useSelector((state: State) => state.auth);
  const offers: Offer[] =
    useSelector((state: State) => selectOffers(state)) || [];
  const {
    bgGradient,
    bgIllustration,
    borderColor,
    offerExpireRange,
    offerValidityRange,
    showDashedBorder,
  } = useSelector(
    (state: State) => state.remoteConfig.configs?.miniOffers || {}
  );
  const isOfferApiV2Enabled = useSelector(
    (state: State) => state.remoteConfig.switches?.isOfferApiV2Enabled || false
  );
  const productId =
    useSelector((state: State) => selectCurrentProductId(state)) || '';
  const { user, dataFetched: authDataFetched } = authData;
  const { email, groupId, isLoyal = false } = user;
  const isMiniCouponsAndOffersEnabled = useMiniCouponsAndOffersEligibility();
  /**
   * ! The logic to fetch offers should occur after react config is fetched
   * * Incase of the new users the PHPSESSID is only available after ReactConfig call without which the offer call fails.
   */
  useEffect(() => {
    window.requestIdleCallback(async () => {
      if (authDataFetched) {
        const deviceType = deviceTypes.D_SITE;
        const domain = platformNames.NYKAA;
        const params = {
          productId,
          customerGroupId: parseInt(groupId) || 0,
          isLoyal,
          domain,
          deviceType,
          fetchUniversalOffers: true,
        };
        dispatch(fetchProductOffers(params, isOfferApiV2Enabled));
      }
    });
  }, [
    productId,
    email,
    isLoyal,
    groupId,
    authDataFetched,
    dispatch,
    isOfferApiV2Enabled,
  ]);

  const handleShowMore = () => {
    if (numberOfCoupons === 1) {
      setNumberOfCoupons(offers.length);
      setTitle('View less');
    } else {
      setNumberOfCoupons(1);
      setTitle('View more offers');
    }
  };
  if (offers && offers.length > 0) {
    return isMiniCouponsAndOffersEnabled ? (
      <>
        <Divider />
        <HeadingWrapper>
          <OfferHeading>{'Offers'}</OfferHeading>
          <Dot
            src={DOT_ICON}
            loading="lazy"
          />
          <OfferText>
            {offers.length} {'available'}
          </OfferText>
        </HeadingWrapper>
        <MiniOffersWrapper>
          {offers.map((offer: Offer) => {
            const {
              title,
              description,
              deltaTextModified,
              id: offerId,
              expiryEpoch,
              status,
            } = offer || {};
            return (
              <OfferTileV2
                offerTitle={title}
                offerSubTitle={description}
                key={offerId}
                id={offerId}
                expiryEpoch={expiryEpoch}
                offerExpireRange={offerExpireRange}
                offerValidityRange={offerValidityRange}
                inlineTitle={true}
                bottomBadgeText={deltaTextModified || ''}
                viewProductLink={
                  toShowViewProducts(
                    offer.isViewProducts,
                    offer.isStatic,
                    offer.id
                  )
                    ? getOfferListingUrl(offer.id, 'pdp')
                    : null
                }
                platform={__PLATFORM__}
                status={status || ''}
                bgIllustration={bgIllustration}
                bgGradient={bgGradient}
                borderColor={borderColor}
                showDashedBorder={showDashedBorder}
                offerTileWidth="270px"
                deviceType={deviceTypes.D_SITE}
              />
            );
          })}
        </MiniOffersWrapper>
        <Divider />
      </>
    ) : (
      <Wrapper id={OFFERS_SECTION_ID}>
        {numberOfCoupons === 1 ? (
          <OfferList>{offers[0]?.description}</OfferList>
        ) : (
          offers?.map((offer) => (
            <OfferList key={offer.id}>{offer?.description}</OfferList>
          ))
        )}
        {offers.length > 1 && (
          <ShowMore
            title={title}
            handleClick={handleShowMore}
          />
        )}
      </Wrapper>
    );
  }

  return <OfferSkeleton />;
};

export default Offers;
